/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  OperationalConfirmModal,
  type OperationalConfirmModalProps,
} from '../modals/OperationalConfirmModal';

export type OperationalConfirmOptions = {
  title: string;
  message?: string;
  confirmLabel?: string;
  destructive?: boolean;
  iconName?: OperationalConfirmModalProps['iconName'];
  /**
   * `overlay`: dentro de otro Modal (ModalShell). Obligatorio si el confirm
   * se dispara desde un sheet ya abierto — si no, en web el botón “no hace nada”.
   */
  presentation?: 'modal' | 'overlay';
};

type PendingConfirm = {
  options: OperationalConfirmOptions;
  resolve: (confirmed: boolean) => void;
};

/**
 * Confirmación con `OperationalConfirmModal` (reemplazo de Alert / window.confirm).
 * Montar `ConfirmModal` en el árbol del screen o layout padre.
 */
export function useOperationalConfirm() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const confirm = useCallback((options: OperationalConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const entry: PendingConfirm = { options, resolve };
      pendingRef.current = entry;
      setPending(entry);
    });
  }, []);

  const close = useCallback(() => {
    const current = pendingRef.current;
    if (!current) return;
    current.resolve(false);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const handleConfirm = useCallback(() => {
    const current = pendingRef.current;
    if (!current) return;
    current.resolve(true);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const ConfirmModal = pending ? (
    <OperationalConfirmModal
      visible
      title={pending.options.title}
      message={pending.options.message ?? ''}
      confirmLabel={pending.options.confirmLabel ?? 'Confirmar'}
      destructive={pending.options.destructive}
      iconName={pending.options.iconName}
      presentation={pending.options.presentation ?? 'modal'}
      onClose={close}
      onConfirm={handleConfirm}
    />
  ) : null;

  return { confirm, ConfirmModal };
}
